<template>
    <div class="relative">
        <!-- ShadCN Dropdown -->
        <Popover v-model:open="isOpen" class="relative">
            <PopoverTrigger as-child>
                <Button
                    variant="outline"
                    size="sm"
                    class="h-8 shrink-0 gap-1.5 border-dashed text-muted-foreground hover:text-foreground"
                >
                    <AppIcon name="line-columns" class="h-4 w-4" />
                    <span v-if="selectedColumns.length">{{ selectedColumns.length }} Columns</span>
                    <span v-else>Columns</span>
                </Button>
            </PopoverTrigger>

            <PopoverContent class="w-[100vw] lg:max-w-[1000px] p-0 shadow-lg" :side="'bottom'" :align="'start'">
                <div class="flex h-[500px] flex-col">
                    <!-- Header -->
                    <div class="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-2">
                        <h3 class="text-md shrink-0 font-medium">Customize columns</h3>
                        <div class="flex min-w-0 flex-1 items-center justify-end gap-2">
                            <div class="relative w-full max-w-xs">
                                <AppIcon
                                    name="search"
                                    class="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                                />
                                <Input
                                    v-model="searchQuery"
                                    type="search"
                                    placeholder="Search columns"
                                    class="h-8 pl-9"
                                />
                            </div>
                            <Button
                                v-if="defaultColumnIds.length"
                                variant="ghost"
                                size="sm"
                                class="h-8 text-muted-foreground hover:text-foreground"
                                :disabled="!canResetColumns"
                                :title="canResetColumns ? 'Reset columns to the default view' : 'Columns are already using the default view'"
                                @click="resetToDefault"
                            >
                                <AppIcon name="rotate-left" class="mr-1.5 h-3.5 w-3.5" />
                                Reset
                            </Button>
                            <Button variant="ghost" size="icon" class="h-7 w-7" @click="isOpen = false">
                                <X class="h-4 w-4 text-slate-500" />
                                <span class="sr-only">Close</span>
                            </Button>
                        </div>
                    </div>

                    <!-- Content -->
                    <div class="flex flex-1 overflow-hidden">
                        <!-- Selected Columns List -->
                        <div class="scrollbar-default w-64 overflow-y-auto border-r border-gray-200 p-4">
                            <h4 class="mb-3 text-sm font-medium text-gray-700">Rearrange columns</h4>

                            <div v-if="selectedColumns.length === 0" class="text-sm italic text-gray-500">
                                No columns selected
                            </div>

                            <div v-else class="flex flex-col space-y-1">
                                <draggable
                                    v-model="selectedColumns"
                                    :item-key="getSelectedColumnKey"
                                    handle=".drag-handle"
                                    :animation="150"
                                    ghost-class="ghost-item"
                                    class="flex flex-col space-y-1"
                                    @end="queueApplyChanges"
                                >
                                    <template #item="{element: columnId }">
                                        <div
                                            class="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                                        >
                                            <div class="flex items-center">
                                                <AppIcon
                                                    name="grip-lines"
                                                    class="drag-handle mr-2.5 h-4 w-4 cursor-move text-gray-400"
                                                />
                                                <div class="flex flex-col">
                                                    <span class="line-clamp-1 text-sm font-medium leading-4">{{
                                                        getColumnById(columnId)?.header?.label || columnId
                                                    }}</span>
                                                    <span v-if="getColumnById(columnId)?.header?.sub_group_label" class="truncate text-xs capitalize leading-none">{{
                                                        getColumnById(columnId)?.header?.sub_group_label
                                                    }}</span>
                                                    <span v-else class="truncate text-xs capitalize leading-none">{{
                                                       getColumnById(columnId)?.group
                                                    }}</span>
                                                </div>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                class="h-5 w-5 text-gray-400 hover:text-red-500"
                                                @click.stop="toggleColumn(columnId)"
                                            >
                                                <AppIcon name="xmark" class="w-4 h-4" />
                                                <span class="sr-only">Remove</span>
                                            </Button>
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>

                        <!-- Column Selection Area -->
                        <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
                            <div class="scrollbar-default flex-1 overflow-y-auto">
                                <div
                                    v-for="group in groups"
                                    :key="group.id"
                                    class="border-b border-gray-200"
                                >
                                    <Collapsible v-model:open="openGroups[group.id]">
                                        <CollapsibleTrigger as-child>
                                            <button
                                                type="button"
                                                class="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-50"
                                            >
                                                <span>{{ group.label }}</span>
                                                <div class="flex items-center gap-2">
                                                    <span
                                                        class="inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-slate-600"
                                                    >
                                                        {{ countSelectedInGroup(group.id) }}
                                                    </span>
                                                    <AppIcon
                                                        name="chevron-down"
                                                        class="h-3 w-3 text-slate-400 transition-transform"
                                                        :class="{ 'rotate-180': openGroups[group.id] }"
                                                    />
                                                </div>
                                            </button>
                                        </CollapsibleTrigger>

                                        <CollapsibleContent>
                                            <div class="px-3 py-4">
                                                <!-- Sub-label groups -->
                                                <div
                                                    v-for="[subLabel, columns] in getColumnsGroupedBySubLabel(group.id)"
                                                    :key="subLabel"
                                                    class="mb-6"
                                                >
                                                    <!-- Show sub_label as a header if it exists -->
                                                    <h5
                                                        v-if="subLabel"
                                                        class="mb-2 border-b border-gray-100 pb-1 text-sm font-medium text-gray-700"
                                                    >
                                                        {{ subLabel }}
                                                    </h5>

                                                    <div class="grid grid-cols-2 gap-2">
                                                        <div
                                                            v-for="column in columns"
                                                            :key="column.id"
                                                            class="flex cursor-pointer items-center rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                                                            @click="toggleColumn(column.id)"
                                                        >
                                                            <div class="mr-3 flex">
                                                                <Checkbox
                                                                    :checked="isColumnSelected(column.id)"
                                                                    class="my-auto bg-white"
                                                                    @click.stop="toggleColumn(column.id)"
                                                                />
                                                            </div>
                                                            <div class="flex min-w-0 flex-1 items-center">
                                                                <!-- <AppIcon
                                                                    :name="getColumnIcon(column)"
                                                                    class="mr-2 h-4 w-4 text-gray-400"
                                                                /> -->
                                                                <div class="truncate text-sm">
                                                                    <span>{{ column.header.label }}</span>
                                                                </div>
                                                            </div>
                                                            <TooltipProvider v-if="column.header.tooltip">
                                                                <Tooltip>
                                                                    <TooltipTrigger as-child>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            class="h-6 w-6"
                                                                            @click.stop
                                                                        >
                                                                            <AppIcon
                                                                                name="info-circle"
                                                                                class="text-gray-400 w-4 h-4"
                                                                            />
                                                                            <span class="sr-only">Info</span>
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p class="font-medium">
                                                                            {{ column.header.tooltip.title }}
                                                                        </p>
                                                                        <p
                                                                            v-if="column.header.tooltip.sub_title"
                                                                            class="text-xs text-gray-500"
                                                                        >
                                                                            {{ column.header.tooltip.sub_title }}
                                                                        </p>
                                                                        <div
                                                                            v-if="column.header.tooltip.content"
                                                                            class="mt-1 text-sm"
                                                                        >
                                                                            <p
                                                                                v-for="(item, i) in column.header.tooltip
                                                                                    .content"
                                                                                :key="i"
                                                                            >
                                                                                {{ item.text }}
                                                                            </p>
                                                                        </div>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div
                                                    v-if="getColumnsGroupedBySubLabel(group.id).length === 0"
                                                    class="py-2 text-center text-sm text-gray-500"
                                                >
                                                    No columns found
                                                </div>
                                            </div>
                                        </CollapsibleContent>
                                    </Collapsible>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </PopoverContent>
        </Popover>
    </div>
</template>

<script>
import { onBeforeUnmount, ref, watch } from 'vue'
import draggable from 'vuedraggable'
import _ from 'lodash'
import { X } from 'lucide-vue-next'
// Import ShadCN components
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'

export default {
    name: 'ColumnDisplayManager',
    components: {
        Popover,
        PopoverContent,
        PopoverTrigger,
        Button,
        Collapsible,
        CollapsibleContent,
        CollapsibleTrigger,
        X,
        Input,
        Checkbox,
        Tooltip,
        TooltipContent,
        TooltipTrigger,
        TooltipProvider,
        draggable
    },
    props: {
        /**
         * All available columns that can be displayed
         */
        availableColumns: {
            type: Array,
            required: true,
        },
        /**
         * Currently selected column IDs
         */
        modelValue: {
            type: Array,
            default: () => [],
        },
        /**
         * Available column groups configuration
         */
        groups: {
            type: Array,
            required: true,
            // Example: [{ id: 'basicInfo', label: 'Basic Info' }]
        },
        /**
         * Default column IDs used by reset-capable tables.
         */
        defaultColumns: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'create-field'],
    setup(props) {
        // Local reactive state
        const isOpen = ref(false)
        const openGroups = ref({})
        const searchQuery = ref('')
        const autoApplyTimer = ref(null)

        const filterAvailableColumns = (columnIds) => {
            const availableColumnIds = new Set(props.availableColumns.map((column) => column.id))

            return _.filter(columnIds, (columnId) => availableColumnIds.has(columnId))
        }

        const syncOpenGroups = (groups) => {
            const nextOpenGroups = {}

            groups.forEach((group, index) => {
                nextOpenGroups[group.id] = openGroups.value[group.id] ?? index < 3
            })

            openGroups.value = nextOpenGroups
        }

        syncOpenGroups(props.groups)

        // Create a local copy of selected columns for editing
        const selectedColumns = ref(filterAvailableColumns(props.modelValue))

        // Watch for changes in the modelValue prop to update local state
        watch(
            [() => props.modelValue, () => props.availableColumns],
            ([newValue]) => {
                const nextSelectedColumns = filterAvailableColumns(newValue)

                if (!_.isEqual(selectedColumns.value, nextSelectedColumns)) {
                    selectedColumns.value = nextSelectedColumns
                }
            },
            { deep: true },
        )

        watch(
            () => props.groups,
            (newGroups) => {
                syncOpenGroups(newGroups)
            },
        )

        watch(searchQuery, (newSearchQuery) => {
            if (newSearchQuery.trim()) {
                props.groups.forEach((group) => {
                    openGroups.value[group.id] = true
                })
            }
        })

        onBeforeUnmount(() => {
            if (autoApplyTimer.value) {
                window.clearTimeout(autoApplyTimer.value)
            }
        })

        return {
            autoApplyTimer,
            isOpen,
            openGroups,
            searchQuery,
            selectedColumns,
        }
    },
    computed: {
        defaultColumnIds() {
            return this.filterColumnIds(this.defaultColumns)
        },

        canResetColumns() {
            return this.defaultColumnIds.length > 0 && !_.isEqual(this.selectedColumns, this.defaultColumnIds)
        },

        normalizedSearchQuery() {
            return this.searchQuery.trim().toLowerCase()
        },
    },
    methods: {
        filterColumnIds(columnIds) {
            const availableColumnIds = _.map(this.availableColumns, column => {
                return column.id
            })

            return _.filter(columnIds, columnId => {
                return availableColumnIds.includes(columnId)
            })
        },

        getSelectedColumnKey(columnId) {
            return columnId
        },

        resetToDefault() {
            this.selectedColumns = [...this.defaultColumnIds]
            this.queueApplyChanges()
        },

        emitSelectedColumns() {
            this.$emit('update:modelValue', [...this.selectedColumns])
        },

        queueApplyChanges() {
            if (this.autoApplyTimer) {
                window.clearTimeout(this.autoApplyTimer)
            }

            this.autoApplyTimer = window.setTimeout(() => {
                this.autoApplyTimer = null
                this.emitSelectedColumns()
            }, 0)
        },

        /**
         * Toggle a column selection
         */
        toggleColumn(columnId) {
            const index = this.selectedColumns.indexOf(columnId)

            if (index === -1) {
                // Add the column to the selection
                this.selectedColumns.push(columnId)
            } else {
                // Remove the column from the selection
                this.selectedColumns.splice(index, 1)
            }

            this.queueApplyChanges()
        },

        /**
         * Check if a column is currently selected
         */
        isColumnSelected(columnId) {
            return this.selectedColumns.includes(columnId)
        },

        /**
         * Get a column by its ID
         */
        getColumnById(columnId) {
            return this.availableColumns.find((column) => column.id === columnId)
        },

        columnMatchesSearch(column) {
            if (!this.normalizedSearchQuery) return true

            return [
                column.id,
                column.header?.label,
                column.header?.sub_label,
                column.header?.sub_group_label,
                column.group,
            ].some((value) => String(value || '').toLowerCase().includes(this.normalizedSearchQuery))
        },

        /**
         * Get all columns that belong to a specific group
         */
        getColumnsByGroup(groupId) {
            return this.availableColumns.filter((column) => {
                return column.group === groupId && this.columnMatchesSearch(column)
            })
        },

        /**
         * Get columns grouped by sub_label within a specific group
         * Maintains original insertion order of sub_labels, with empty sub_labels first
         */
        getColumnsGroupedBySubLabel(groupId) {
            const columns = this.getColumnsByGroup(groupId)

            // Create an object to store columns by sub_label
            const groupedBySubLabel = {}

            // Track sub_label order (excluding empty sub_label)
            const subLabelOrder = []

            // Group columns by sub_label and track order
            columns.forEach((column) => {
                const subLabel = column.header.sub_label || ''

                // Initialize the array if this is the first time we see this sub_label
                if (!groupedBySubLabel[subLabel]) {
                    groupedBySubLabel[subLabel] = []

                    // Add to order tracking (but not empty sub_label)
                    if (subLabel !== '' && !subLabelOrder.includes(subLabel)) {
                        subLabelOrder.push(subLabel)
                    }
                }

                groupedBySubLabel[subLabel].push(column)
            })

            // Convert to array of groups with empty sub_label first, then others in original order
            const result = []

            // Add empty sub_label group first (if it exists)
            if (groupedBySubLabel['']) {
                result.push(['', groupedBySubLabel['']])
            }

            // Add other sub_labels in their original order
            subLabelOrder.forEach((subLabel) => {
                result.push([subLabel, groupedBySubLabel[subLabel]])
            })

            return result
        },

        /**
         * Count how many columns are selected within a group
         */
        countSelectedInGroup(groupId) {
            const groupColumns = this.availableColumns.filter((column) => column.group === groupId)
            return groupColumns.filter((column) => this.selectedColumns.includes(column.id)).length
        },

        /**
         * Get an appropriate icon for a column based on its properties
         */
        getColumnIcon(column) {
            if (!column) return 'table-column'

            // Map column types to application icons
            const iconMap = {
                creator: 'user',
                followers: 'users',
                subscribers: 'users',
                language: 'language',
                country: 'globe',
                region: 'globe',
                gender: 'venus-mars',
                age_range: 'birthday-cake',
                avg_viewers: 'eye',
                peak_viewers: 'chart-line',
                engagement_rate: 'percentage',
                avg_likes: 'thumbs-up',
                sum_likes: 'thumbs-up',
                peak_likes: 'thumbs-up',
                avg_shares: 'share',
                avg_quotes: 'quote-left',
                avg_comments: 'comment',
                sum_comments: 'comments',
                peak_comments: 'comment',
                total_games_played: 'gamepad',
                game_primary: 'gamepad',
                game_secondary: 'gamepad',
                game_tertiary: 'gamepad',
                content_count: 'film',
                total_streams: 'video',
                avg_views: 'eye',
                sum_views: 'eye',
                peak_views: 'eye',
                avg_impressions: 'chart-bar',
                sum_impressions: 'chart-bar',
                avg_minutes_watched: 'clock',
                sum_minutes_watched: 'clock',
                avg_airtime: 'broadcast-tower',
                sum_airtime: 'broadcast-tower',
                peak_airtime: 'broadcast-tower',
                account_rating_remapped: 'star',
                main_industry: 'industry',
                main_industry_category: 'industry',
                affinity: 'percentage',
                // Audience demographics
                'audience.gender.male': 'mars',
                'audience.gender.female': 'venus',
                'audience.gender.non_binary': 'venus-mars',
                'audience.top_country': 'globe',
                'audience.top_language': 'language',
                'audience.top_ethnicity': 'users',
                'audience.top_income': 'dollar-sign',
                'audience.top_education': 'graduation-cap',
                'audience.top_family_status': 'house-user',
                'audience.top_engagement_style': 'comments',
            }

            // Check for exact match first
            if (iconMap[column.id]) {
                return iconMap[column.id]
            }

            // Check for audience age range columns
            if (column.id?.startsWith('audience.age_ranges.')) {
                return 'birthday-cake'
            }

            return 'table-column'
        },
    },
}
</script>

<style>
.ghost-item {
    background-color: #EFF6FF !important; /* bg-blue-50 equivalent */
    border-color: #93C5FD !important; /* border-blue-300 equivalent */
    opacity: 0.8;
}
</style>
